import React, { useEffect, useState } from 'react';
import { Collapse, Tag, Empty, Spin, Alert, Card, Row, Col, Typography, Space, Badge } from 'antd';
import { GiftOutlined, TrophyOutlined, TeamOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import referralService from '../../../../services/referral/referral.service';
import { ReferralHistoryV2, RewardStatus, RewardType } from '../../../../types/referral.types';
import { formatRewardValue, getRewardTypeLabel } from '../../../../utils/referralUtils';
import moment from 'moment';

const { Panel } = Collapse;
const { Title, Text } = Typography;

const ReferralHistory: React.FC = () => {
  const [history, setHistory] = useState<ReferralHistoryV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await referralService.getHistory();
      setHistory(data);
      // İlk kodun panelini otomatik aç
      if (data.length > 0) {
        setActiveKeys([data[0].code]);
      }
    } catch (err: any) {
      setError('Geçmiş yüklenirken bir hata oluştu.');
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRewardStatusColor = (status: RewardStatus) => {
    switch (status) {
      case RewardStatus.EARNED:
        return 'success';
      case RewardStatus.USED:
        return 'blue';
      case RewardStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getRewardStatusLabel = (status: RewardStatus) => {
    switch (status) {
      case RewardStatus.EARNED:
        return 'Kazanıldı';
      case RewardStatus.USED:
        return 'Kullanıldı';
      case RewardStatus.CANCELLED:
        return 'İptal Edildi';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Hata"
        description={error}
        type="error"
        showIcon
        action={
          <button onClick={fetchHistory} style={{ marginLeft: '8px', padding: '4px 8px', cursor: 'pointer' }}>
            Tekrar Dene
          </button>
        }
      />
    );
  }

  if (history.length === 0) {
    return (
      <Empty
        description="Henüz referans geçmişiniz bulunmamaktadır"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 50%, #c77dff 100%)',
        borderRadius: '16px',
        padding: '32px 24px',
        marginBottom: '32px',
        textAlign: 'center',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(115, 56, 161, 0.3)'
      }}>
        <div style={{
          display: 'inline-block',
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '50%',
          marginBottom: '16px',
          backdropFilter: 'blur(10px)'
        }}>
          <TrophyOutlined style={{ fontSize: '48px', color: 'white' }} />
        </div>
        <Title level={2} style={{ color: 'white', marginBottom: '8px', fontWeight: 'bold' }}>
          Referans Geçmişim
        </Title>
        <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
          Tüm kampanyalardan oluşturduğunuz kodlar ve kazandığınız ödüller
        </Text>
      </div>

      {/* Kodlar Listesi - Collapse ile */}
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys as string[])}
        expandIconPosition="end"
        style={{
          background: 'transparent',
          border: 'none'
        }}
      >
        {history.map((item) => (
          <Panel
            key={item.code}
            header={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <div style={{
                    padding: '8px 12px',
                    background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 100%)',
                    borderRadius: '8px',
                    color: 'white',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    {item.code}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#262626' }}>
                      {item.campaignName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                      <CalendarOutlined /> {moment(item.createdAt).format('DD.MM.YYYY')}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <Badge
                    count={item.timesUsed}
                    style={{ backgroundColor: '#52c41a' }}
                    title={`${item.timesUsed} kez kullanıldı`}
                  >
                    <Tag color="green" icon={<TeamOutlined />}>
                      {item.timesUsed} Davet
                    </Tag>
                  </Badge>
                  <Badge
                    count={item.earnedRewards.length}
                    style={{ backgroundColor: '#1890ff' }}
                    title={`${item.earnedRewards.length} ödül kazanıldı`}
                  >
                    <Tag color="blue" icon={<TrophyOutlined />}>
                      {item.earnedRewards.length} Ödül
                    </Tag>
                  </Badge>
                </div>
              </div>
            }
            style={{
              marginBottom: '16px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e8e8e8',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <div style={{ padding: '16px 0' }}>
              {/* Davet Edilen Şirketler */}
              <Card
                title={
                  <Space>
                    <TeamOutlined style={{ color: '#7338a1' }} />
                    <span>Davet Edilen Şirketler ({item.referredCompanies.length})</span>
                  </Space>
                }
                style={{
                  marginBottom: '24px',
                  borderRadius: '8px',
                  border: '1px solid #e8e8e8'
                }}
                bodyStyle={{ padding: '16px' }}
              >
                {item.referredCompanies.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {item.referredCompanies.map((company) => (
                      <Col xs={24} sm={12} md={8} key={company.id}>
                        <Card
                          size="small"
                          style={{
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                            border: '1px solid #bae6fd',
                            borderRadius: '8px'
                          }}
                          bodyStyle={{ padding: '12px' }}
                        >
                          <Text strong style={{ color: '#0369a1' }}>
                            {company.name}
                          </Text>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty
                    description="Henüz bu kodla davet edilen şirket bulunmamaktadır"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '20px 0' }}
                  />
                )}
              </Card>

              {/* Kazanılan Ödüller */}
              <Card
                title={
                  <Space>
                    <TrophyOutlined style={{ color: '#f59e0b' }} />
                    <span>Kazanılan Ödüller ({item.earnedRewards.length})</span>
                  </Space>
                }
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e8e8e8'
                }}
                bodyStyle={{ padding: '16px' }}
              >
                {item.earnedRewards.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {item.earnedRewards.map((reward, index) => (
                      <Col xs={24} sm={12} lg={8} key={index}>
                        <Card
                          size="small"
                          style={{
                            background: reward.status === RewardStatus.EARNED
                              ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                              : reward.status === RewardStatus.USED
                              ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                              : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                            border: reward.status === RewardStatus.EARNED
                              ? '1px solid #86efac'
                              : reward.status === RewardStatus.USED
                              ? '1px solid #93c5fd'
                              : '1px solid #d1d5db',
                            borderRadius: '8px'
                          }}
                          bodyStyle={{ padding: '16px' }}
                        >
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Text strong style={{
                                fontSize: '18px',
                                color: reward.status === RewardStatus.EARNED ? '#16a34a' : reward.status === RewardStatus.USED ? '#2563eb' : '#6b7280'
                              }}>
                                {formatRewardValue(reward.type, reward.value)}
                              </Text>
                              <Tag
                                color={getRewardStatusColor(reward.status)}
                                icon={reward.status === RewardStatus.EARNED ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
                              >
                                {getRewardStatusLabel(reward.status)}
                              </Tag>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                Ödül Türü:
                              </Text>
                              <div style={{ marginTop: '4px' }}>
                                <Tag color="purple">
                                  {getRewardTypeLabel(reward.type)}
                                </Tag>
                              </div>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                Kazanıldığı Şirket:
                              </Text>
                              <div style={{ marginTop: '4px', fontWeight: 600, color: '#262626' }}>
                                {reward.earnedFrom.name}
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty
                    description="Henüz bu kodla kazanılan ödül bulunmamaktadır"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '20px 0' }}
                  />
                )}
              </Card>
            </div>
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

export default ReferralHistory;
